// Traducción inglesa de los servicios. Cada objeto se empareja por slug con
// el español y sustituye sus campos. No incluir: category, ogImage,
// relatedServices, relatedProblems, relatedGuides (se heredan del español).
export const servicesEn = [
  {
    slug: "medicion-fincas-terrenos",
    shortName: "Plot and land surveying",
    name: "Plot and land surveying",
    seoTitle: "Plot and land surveying in the Canary Islands | MideFinca",
    metaDescription:
      "Professional surveying of plots, parcels and land in the Canary Islands with GNSS and total station. Know the real surface area of your property with clear technical documentation.",
    excerpt: "Know the real surface area and geometry of your plot with GNSS surveying and a technical plan.",
    h1: "Plot and land surveying in the Canary Islands",
    intro:
      "Knowing the real surface area of a plot is the starting point for selling, buying, dividing an inheritance or correcting Catastro data. We measure your parcel with GNSS receivers and total station, and hand over a plan with the true extent, vertices and full geometry.",
    when: [
      "You are selling or buying and want to know how many square metres the plot really has.",
      "Catastro, the title deed and reality don't add up and you need an objective measurement.",
      "You have inherited a plot and nobody knows its exact surface area or boundaries.",
      "You need a reliable survey as the basis for a GML file, georeferencing or a boundary demarcation.",
      "You want to divide or merge parcels and need to know the starting extent.",
    ],
    solves:
      "Most plot problems start the same way: nobody really knows how many square metres there are. Catastro says one figure, the deed another and the fence marks something else. A professional survey replaces assumptions with data measured on the ground, and it is the technical basis for any subsequent procedure.",
    includes: [
      "Site visit and measurement with GNSS receivers and, where coverage or precision requires it, total station.",
      "Location and coordinate capture of the parcel vertices.",
      "Plan with the measured geometry, resulting surface area and perimeter.",
      "Technical report describing the work carried out.",
      "Comparison with the Catastro and title deed surface areas, if you provide the documentation.",
      "Measurement certificate.",
    ],
    needs: [
      "The cadastral reference of the plot (if you have it).",
      "Title deed or Land Registry extract, if available.",
      "Access to the plot on the day of the survey.",
      "Any information about known boundaries: markers, fences, walls.",
    ],
    receive: [
      "Plan of the plot with the measured surface area, in PDF.",
      "List of vertex coordinates.",
      "Technical report with the methodology and equipment used.",
      "Surface area comparison: measured, cadastral and registered.",
      "Measurement certificate.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "How much does it cost to survey a plot?",
        a: "It depends on the surface area, terrain, vegetation, access and existing documentation. That's why we don't publish generic rates: tell us about your case and we'll give you a fixed quote before starting.",
      },
      {
        q: "Can the survey be used to amend Catastro?",
        a: "The survey is the technical evidence of the real surface area, but each Catastro procedure has its own requirements. We'll tell you what can be done with your documentation and what steps would follow.",
      },
      {
        q: "How long does it take?",
        a: "A simple plot can be measured in a single visit. The overall timeframe depends on our schedule and the size of the job; we confirm it in the quote.",
      },
    ],
  },
  {
    slug: "levantamiento-topografico",
    shortName: "Topographic survey",
    name: "Topographic survey",
    seoTitle: "Topographic surveys in the Canary Islands | MideFinca",
    metaDescription:
      "Topographic surveys of plots and land in the Canary Islands: plans with contour lines, elevations and existing features for projects, construction and technical documentation.",
    excerpt: "Detailed topographic plans with contour lines, elevations and existing features, ready to design on.",
    h1: "Topographic surveys in the Canary Islands",
    intro:
      "A topographic survey is not limited to representing the plot in plan view: it also records the heights or levels of the terrain and of the existing features. Levels are, simply put, the height of each point measured against a reference. With them you know the elevation changes, slopes, walls, platforms, buildings and other elements needed to design with precision, and the result is a topographic plan ready to design on a measured reality, not an assumed one.",
    when: [
      "An architect or engineer is going to design on your plot and asks you for a survey.",
      "You need to know slopes, elevations and level changes before building or landscaping.",
      "You want to document the current state of a plot with all its features.",
      "You are submitting a project and the administration requires a georeferenced topographic plan.",
    ],
    solves:
      "Designing on old or approximate plans is expensive: elevation errors, walls that aren't where they should be, misjudged slopes. The topographic survey gives the designer a measured, georeferenced base and prevents surprises during construction.",
    includes: [
      "Fieldwork with GNSS and total station.",
      "Capture of elevations and features: buildings, walls, tracks, relevant trees, visible services.",
      "Topographic plan with contour lines at a scale suited to its use.",
      "Georeferencing of the plan in the official system (ETRS89 / UTM zone 28).",
      "Delivery in PDF and CAD formats (DWG/DXF) if requested.",
    ],
    needs: [
      "Location and cadastral reference of the parcel.",
      "Access to the land on the fieldwork day.",
      "The intended use of the plan (project, construction, documentation), to adjust scale and detail.",
    ],
    receive: [
      "Georeferenced topographic plan in PDF.",
      "CAD file (DWG/DXF) if your designer needs it.",
      "Contour lines, elevations and existing features represented.",
      "Measurement certificate.",
    ],
    faq: [
      {
        q: "How does it differ from a plot survey?",
        a: "A plot survey focuses on surface area and boundaries. The topographic survey adds the relief: elevations, contour lines and every feature of the land, designed for project work.",
      },
      {
        q: "Is the plan valid for submitting a project?",
        a: "It is the base that projects usually require, delivered georeferenced. Each administration's specific requirements may vary; if your architect gives us specifications, we follow them.",
      },
    ],
  },
  {
    slug: "georreferenciacion-fincas",
    shortName: "Plot georeferencing",
    name: "Plot georeferencing",
    seoTitle: "Plot georeferencing in the Canary Islands | MideFinca",
    metaDescription:
      "Georeferencing of plots and parcels in the Canary Islands: official coordinates of your property's vertices, the basis for GML files, Catastro and registry graphic representation.",
    excerpt: "Your vertices in official coordinates: the basis for GML files, Catastro, the notary, the Land Registry and any graphic procedure.",
    h1: "Plot georeferencing in the Canary Islands",
    intro:
      "Georeferencing a plot means placing its vertices in official coordinates (ETRS89, zone 28 in the Canary Islands), so its geometry is anchored to the territory rather than to ambiguous descriptions. It is the technical basis of GML files and of any graphic representation before Catastro or the Land Registry.",
    when: [
      "You have been asked for the plot's georeferencing for a deed or a procedure.",
      "You are going to submit a GML to Catastro or the Land Registry and need measured coordinates.",
      "The cadastral graphic representation doesn't match reality and you want to prove the correct geometry.",
      "You need to relate your parcel to the official mapping beyond doubt.",
    ],
    solves:
      "Old deeds describe plots by adjoining properties ('to the north, lands of…') which today locate nothing. Georeferencing translates the property into measured coordinates, comparable with Catastro and defensible in any proceeding.",
    includes: [
      "Measurement of the plot vertices with GNSS and total station.",
      "Computation and adjustment of coordinates in the official reference system.",
      "Georeferenced plan of the parcel.",
      "List of vertex coordinates, ready to generate the GML file if commissioned.",
    ],
    needs: [
      "Cadastral reference.",
      "Title deed or registry extract, if available.",
      "Access to the plot and indication of known boundaries.",
    ],
    receive: [
      "Georeferenced plan with the plot's geometry.",
      "Table of vertex coordinates in ETRS89 / UTM 28.",
      "If commissioned, the validated GML file for Catastro and the Land Registry.",
      "Detailed report.",
      "Measurement certificate.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Is georeferencing the same as measuring?",
        a: "They go together: first the vertices are measured on the ground, then their official coordinates are computed. Georeferencing is the result of anchoring that measurement to the cartographic system.",
      },
      {
        q: "Does this change anything in Catastro automatically?",
        a: "No. Georeferencing is a technical document. Amending cadastral data requires its own procedure, in which this documentation is usually the starting technical evidence.",
      },
    ],
  },
  {
    slug: "gml-catastro-registro",
    shortName: "GML for Catastro and Land Registry",
    name: "GML file for Catastro and Land Registry",
    seoTitle: "GML files for Catastro and Land Registry in the Canary Islands | MideFinca",
    metaDescription:
      "Preparation of GML files for plots in the Canary Islands according to Catastro specifications: for deeds, notaries, the Land Registry and graphic representation.",
    excerpt: "The GML file the notary or Land Registry asks you for, produced from real measurement and validated.",
    h1: "GML file for Catastro and Land Registry in the Canary Islands",
    intro:
      "The GML is the file with your plot's georeferenced geometry that notaries and registrars have required since Act 13/2015 to incorporate the graphic representation into the deed. We produce it from real measurement and validate it against the specifications of the Directorate-General of Catastro.",
    when: [
      "The notary has asked you for a GML to sign a purchase, inheritance or segregation.",
      "The registrar requires the georeferenced graphic representation of the plot.",
      "You are going to declare new construction and need the geometry of the parcel and the building.",
      "You want Catastro to draw your plot where and as it really is.",
    ],
    solves:
      "A badly made GML, drawn over an orthophoto without measuring, with incorrect topology or unvalidated, gets rejected and delays the signing. We start from the real measurement of the plot, build the geometry according to the cadastral specifications and validate it before delivery.",
    includes: [
      "Measurement and georeferencing of the plot vertices.",
      "Generation of the GML file according to Catastro specifications.",
      "Validation of the file with the official tools.",
      "Plan of the resulting graphic representation.",
    ],
    needs: [
      "The cadastral reference of the plot.",
      "The deed or the notary/registrar's request, if you have it.",
      "Access to the plot for the measurement.",
    ],
    receive: [
      "Validated GML file, ready to submit to the notary or Land Registry.",
      "Plan with the graphic representation of the plot.",
      "The list of coordinates used.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Will the notary accept this GML?",
        a: "We produce the GML according to the official specifications and validate it with Catastro's tools. Incorporating it into the file is up to the notary and the Land Registry, but the file meets the format they require.",
      },
      {
        q: "Does the same GML file work for Catastro and the Land Registry?",
        a: "The GML format allows the georeferenced geometry of a parcel to be technically represented. The documentation that must accompany it depends on the specific procedure and, in certain cases, it may also be necessary to obtain a positive Informe de Validación Gráfica Alternativa (IVGA, an alternative graphic validation report). We review the case before preparing the documentation.",
      },
      {
        q: "How long does it take?",
        a: "It depends on the measurement schedule and the complexity of the plot. We confirm it in the quote. If you have a planned signing date at the notary or any other specific deadline, tell us from the first contact.",
      },
    ],
  },
  {
    slug: "linderos-deslindes",
    shortName: "Boundaries and demarcations",
    name: "Boundary checking and demarcation",
    seoTitle: "Plot boundaries and demarcations in the Canary Islands | MideFinca",
    metaDescription:
      "Checking and setting out plot boundaries in the Canary Islands. We locate the real limits of your parcel with technical measurement and clear documentation.",
    excerpt: "Locate and mark the real limits of your plot with technical measurement, not assumptions.",
    h1: "Boundary checking and demarcation in the Canary Islands",
    intro:
      "Knowing where a plot begins and ends seems simple until you need to locate it physically on the ground. We download the cadastral representation of the parcel and, during the visit, we set out its vertices so you can identify on the ground where the points shown in the cadastral mapping lie. If there is a discrepancy between Catastro, the title deed, the Land Registry, the fencing or the physical reality, the work may require an additional boundary and documentation study: cadastral mapping on its own does not necessarily determine the legal boundary of a property.",
    when: [
      "You don't know exactly where your plot's boundaries are.",
      "A neighbour has moved a fence or disputes the boundary.",
      "You are going to enclose, fence or build and need certainty about the limits before spending.",
      "Markers or boundary stones have disappeared and need to be set out again.",
    ],
    solves:
      "This service allows you to physically locate how far Catastro represents your plot as reaching, to identify its vertices on the ground and to have references for later fencing or marking. It also makes it possible to detect possible differences between the physical reality and the cadastral representation, and to know whether a fuller measurement or demarcation job is needed afterwards.",
    includes: [
      "Visit to the plot.",
      "Download of the parcel and the available cadastral mapping.",
      "On-site setting out of the vertices of the cadastral representation.",
      "Plan of the set-out representation with its coordinates.",
      "Technical report with the differences detected, if any.",
    ],
    needs: [
      "Title deed or registry extract of the plot.",
      "Cadastral reference.",
      "Access to the parcel and, if possible, to the boundaries from both sides.",
    ],
    receive: [
      "Vertices located during the visit, so you can physically materialise or identify the points with your own marking.",
      "Plan of the cadastral representation with the vertex coordinates.",
      "Technical report with the differences detected between the mapping and the physical reality, if any.",
    ],
    faq: [
      {
        q: "Can you force the neighbour to accept the boundary?",
        a: "No. Our work is technical: we measure and document where the limit lies according to the documentation and the measurement. The neighbour's acceptance or a possible judicial demarcation are different paths, involving other professionals.",
      },
      {
        q: "What if the measurement doesn't match the current fence?",
        a: "That is frequent. We hand over the measured data and the comparison; from there, the solution may involve an agreement between owners or legal action, and the technical documentation is the basis in both cases.",
      },
      {
        q: "Who physically places the vertex markers?",
        a: "On the day of the visit, the owner must have the elements they want to mark the points with: rods, stakes, blocks, stones or another suitable system. We locate each vertex with the measurement equipment and the owner can materialise the points on the ground as we go.",
      },
      {
        q: "Are cadastral points always the legal boundary of my property?",
        a: "Not necessarily. Catastro holds a graphic representation of the parcel, but there can be differences with the title deed, the Land Registry, the existing physical limits or the measured reality. If we detect a discrepancy, we will tell you what study or procedure may be needed to clarify it.",
      },
    ],
  },
  {
    slug: "correccion-discrepancias-catastrales",
    shortName: "Correction of cadastral discrepancies",
    name: "Correction of cadastral discrepancies",
    seoTitle: "Correction of cadastral discrepancies in the Canary Islands | MideFinca",
    metaDescription:
      "Catastro doesn't match your real plot? We study the surface or geometry discrepancy and produce the technical documentation that proves the measured reality.",
    excerpt: "Full technical study when Catastro, title deed and reality don't add up.",
    h1: "Correction of cadastral discrepancies in the Canary Islands",
    intro:
      "Catastro drawing your plot with different square metres or in a different place is more common than it seems. We study the origin of the discrepancy (an old measurement, mapping errors or deeds granted by apparent extent) and produce the technical documentation that proves the measured reality.",
    when: [
      "Catastro attributes more or fewer square metres to your plot than it really has.",
      "The cadastral graphic representation is displaced or doesn't look like your parcel.",
      "The deed and Catastro say different things and you don't know which reflects reality.",
      "A procedure has been denied or complicated by incorrect cadastral data.",
    ],
    solves:
      "Before correcting anything you need to know which figure is right. We measure the plot, reconstruct the documentary history and compare the three sources: reality, Catastro and title. With that diagnosis you will know which procedure may apply and what technical evidence you need.",
    includes: [
      "Measurement and georeferencing of the plot.",
      "Comparative study: real measurement vs. Catastro vs. deed.",
      "Technical report diagnosing the discrepancy and its probable origin.",
      "Graphic documentation (plan and, where applicable, GML) proving the measured reality.",
      "Preparation of the necessary graphic representation and obtaining of a positive Informe de Validación Gráfica Alternativa (IVGA) when needed to raise the cadastral correction.",
    ],
    needs: [
      "Cadastral reference.",
      "The deed and, if you have them, earlier deeds or old titles.",
      "Access to the plot.",
    ],
    receive: [
      "Technical report on the discrepancy with comparative plans.",
      "Georeferenced measurement of the plot.",
      "Guidance on the channels that may apply to your case.",
      "Measurement certificate.",
      "Georeferenced plans.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Will you get Catastro to change the data?",
        a: "We carry out the measurement, produce the technical documentation and prepare the graphic proposal needed to request the correction. From there, it is up to Catastro to process the procedure and resolve it. When the procedure affects adjoining parcels, Catastro may carry out the communications or actions that legally correspond regarding the affected owners. The final decision always belongs to the Administration.",
      },
      {
        q: "What if the one that's wrong is my deed?",
        a: "That happens too: many old deeds were granted by apparent extent. The report identifies which source departs from the measured reality, and from there we assess the possible scenarios with you.",
      },
    ],
  },
  {
    slug: "comprobacion-finca-antes-comprar",
    shortName: "Plot check before buying",
    name: "Plot check before buying",
    seoTitle: "Checking a plot before buying in the Canary Islands | MideFinca",
    metaDescription:
      "Buy a plot in the Canary Islands knowing what you're buying: real surface measurement, boundary check and comparison with Catastro and deed before signing.",
    excerpt: "Measure and verify the plot before signing: real surface area, boundaries and documentary consistency.",
    h1: "Plot check before buying in the Canary Islands",
    intro:
      "Buying a plot without knowing how many square metres it really has is signing blind. Before the purchase we measure the parcel, check its boundaries and compare reality with Catastro and the deed, so you know exactly what you are buying.",
    when: [
      "You are going to buy rural land or a parcel and want to verify the advertised square metres.",
      "The listing, the deed and Catastro state different figures.",
      "You want to know where the boundaries really are before paying.",
      "You are buying to build or develop and need real data about the land.",
    ],
    solves:
      "Surface area differences are almost always discovered after buying, when correcting them is more expensive and there is no room to negotiate. A prior check gives you data to negotiate the price, demand corrections from the seller or walk away from the deal in time.",
    includes: [
      "Measurement of the plot and verification of its real surface area.",
      "Review of the consistency between reality, Catastro and deed.",
      "Boundary check and detection of possible encroachments by neighbours.",
      "Technical report with clear conclusions for your purchase decision.",
    ],
    needs: [
      "Cadastral reference or address of the plot.",
      "Authorisation or accompaniment by the seller or the agency to access it.",
      "Available documentation: registry extract, the seller's deed.",
    ],
    receive: [
      "Verification report with the measured surface area and the discrepancies found.",
      "Plan of the plot with measured boundaries.",
      "Conclusions and points to review before signing.",
    ],
    faq: [
      {
        q: "Do I need the seller's permission?",
        a: "Yes, to measure on the ground we need access to the plot, usually arranged by the seller or the agency. It is a reasonable request: a seller who refuses it is giving you valuable information.",
      },
      {
        q: "How long does the check take?",
        a: "On simple plots, the fieldwork is usually done in one visit and the report within a few days. If you have a date for the deposit contract or the signing, tell us when you contact us.",
      },
    ],
  },
{
    slug: "segregacion-finca-canarias",
    shortName: "Plot segregation",
    name: "Plot segregation",
    seoTitle: "Plot segregation in the Canary Islands | MideFinca",
    metaDescription:
      "Technical documentation to segregate a plot in the Canary Islands: definition of the new parcels, measurement, coordinates and graphic representation for the town hall, Catastro, the notary and the Land Registry, as applicable.",
    excerpt: "We divide a plot into two or more independent parcels, with their new geometries, surface areas and coordinates technically defined.",
    h1: "Plot segregation in the Canary Islands",
    intro:
      "We divide an existing plot into two or more independent parcels, technically defining their new geometries, surface areas and coordinates. We review the available documentation and prepare the graphic representation needed for the procedures before the town hall, Catastro, the notary and the Land Registry, as applicable.",
    when: [
      "You want to split a plot between heirs or co-owners.",
      "You are going to sell only part of your parcel.",
      "You need to separate a portion of the plot for a specific transaction.",
      "The current plot is too large for the use you want to give it.",
    ],
    solves:
      "A segregation starts by knowing exactly what is being divided and how: where the new boundaries run, what surface area each parcel ends up with and what coordinates the new vertices have. Without that prior technical definition, the notarial, registry or cadastral procedure has nothing to work on.",
    includes: [
      "Review of the available documentation: title deed, Land Registry and Catastro.",
      "Measurement and georeferencing of the current plot.",
      "Technical definition of the new parcels: geometries, surface areas and boundaries.",
      "Plans of each resulting parcel.",
      "Graphic representation and GML file of the new parcels, where applicable.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    needs: [
      "The plot's title deed and cadastral reference.",
      "A Land Registry extract (nota simple), if you have one.",
      "An indication of the intended split between the new parcels.",
      "Access to the plot for the measurement.",
    ],
    receive: [
      "Technical report with the definition of the new parcels.",
      "Plans of each resulting parcel with its surface area.",
      "Georeferenced documentation and GML file, where applicable.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Can every plot be segregated?",
        a: "No. Segregation is conditional on compliance with planning regulations and on the administrative authorisations that may be necessary in each municipality. That is why we study the plot before confirming the operation is viable: we do not assume a segregation can be done without having checked it.",
      },
      {
        q: "Who is involved in a segregation?",
        a: "Depending on the case: the town hall when planning regulations require authorisation or notification, the notary for the deed of division, the Land Registry for the registration of the resulting properties and Catastro for the graphic update. Our part is the technical documentation and the graphic representation.",
      },
      {
        q: "How many parcels can come out of my plot?",
        a: "It depends on the applicable planning regulations, the minimum required surface area, the available frontages and access, and the conditions of the plot. We check this in the initial review before proposing anything.",
      },
    ],
  },
  {
    slug: "agrupacion-fincas-canarias",
    shortName: "Plot grouping",
    name: "Plot grouping",
    seoTitle: "Plot grouping in the Canary Islands | MideFinca",
    metaDescription:
      "Technical documentation to group several plots in the Canary Islands: new geometry and surface area of the resulting plot and graphic representation for Catastro, the notary and the Land Registry, where applicable.",
    excerpt: "We join two or more plots into a single resulting plot, with its new geometry, surface area and graphic representation.",
    h1: "Plot grouping in the Canary Islands",
    intro:
      "We join two or more plots into a single resulting plot, defining its new geometry, surface area and graphic representation. We check the available documentation and produce the technical documentation needed to coordinate the operation with Catastro, the notary and the Land Registry where applicable.",
    when: [
      "You have two or more adjoining parcels and want to manage them as one.",
      "You bought the neighbouring plot and want to join it to yours.",
      "You need a single plot for a specific transaction or project.",
      "You want to simplify the documentation of several parcels you own.",
    ],
    solves:
      "Grouping plots is not adding surface areas on paper: the geometry of the resulting plot must be defined, the parcels must be checked to be adjoining and compatible, and the property's new legal and graphic reality must be documented so that Catastro, the deed and the Land Registry all reflect the same thing.",
    includes: [
      "Review of the plots' documentation: title deeds, Land Registry and Catastro.",
      "Measurement and georeferencing of the whole.",
      "Definition of the geometry and surface area of the resulting plot.",
      "Plans of the previous situation and of the grouped plot.",
      "Graphic representation and GML file, where applicable.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    needs: [
      "Title deeds and cadastral references of the plots to be grouped.",
      "Land Registry extracts, if you have them.",
      "Access to the parcels for the measurement.",
    ],
    receive: [
      "Technical report of the grouping.",
      "Plans of the resulting plot with its new surface area.",
      "Georeferenced documentation and GML file, where applicable.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Can all plots be grouped?",
        a: "Not automatically. It must be checked that they are adjoining, the ownership and registered charges must be reviewed and the applicable regulations must be checked. We study this before confirming the viability of the operation.",
      },
      {
        q: "Who formalises the grouping?",
        a: "The grouping is normally formalised by deed before a notary and registered at the Land Registry; afterwards the representation in Catastro is updated. We provide the measurement, the plans and the graphic representation those procedures need.",
      },
    ],
  },
  {
    slug: "declaracion-obra-nueva",
    shortName: "Declaración de obra nueva por antigüedad",
    name: "Declaración de obra nueva por antigüedad",
    seoTitle: "Declaración de obra nueva por antigüedad in the Canary Islands | MideFinca",
    metaDescription:
      "Technical documentation for the declaración de obra nueva por antigüedad (registration of an existing building based on its proven age) in the Canary Islands: measurement, plans, technical report and graphic documentation for Catastro and the Land Registry.",
    excerpt: "Measurement, plans and technical report of your construction for the declaración de obra nueva por antigüedad, when its legal requirements are met.",
    h1: "Declaración de obra nueva por antigüedad in the Canary Islands",
    intro:
      "You have a construction that is not correctly recorded in the title deed: a dwelling, a tool shed, a lounge, a premises, a house on rural land, a porch or another building. When the legal requirements are met, the declaración de obra nueva por antigüedad (registration of an existing building based on its proven age) allows the construction to be technically documented for its subsequent notarial formalisation and registration at the Land Registry. The work may include measurement, plans, a technical report, a measurement certificate, proof of age and the graphic documentation needed for Catastro and the Land Registry. For dwellings, depending on the case, documentation on habitability conditions may also be required.",
    when: [
      "There is a construction on your plot that appears neither in the deed nor in Catastro.",
      "You are going to sell and the buyer or their bank asks for the construction to be documented.",
      "You have inherited a plot with undeclared buildings.",
      "You want to tidy up your property's documentation before the problem arises.",
    ],
    solves:
      "The declaration of new construction is signed before the notary and registered by the Land Registry; our part is the technical one: measuring the construction, drawing it, georeferencing it and producing the certificate describing what exists, how much it measures and since when it is known to exist, according to the available evidence.",
    includes: [
      "Technical report.",
      "Measurement of the construction and the parcel.",
      "Plans of the building: surface area, footprint and position on the plot.",
      "Measurement certificate.",
      "Certificate of age, where applicable.",
      "Habitability documentation, for dwellings and where applicable.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA) or equivalent cadastral graphic documentation, where applicable.",
    ],
    needs: [
      "The plot's deed and cadastral reference.",
      "Access to the plot and the construction.",
      "Any document proving the age of the work (old photos, invoices, permits), if available.",
    ],
    receive: [
      "Technical report.",
      "Measurement certificate.",
      "Certificate of age.",
      "Certificate or habitability documentation, where applicable.",
      "Plans.",
      "Georeferenced documentation.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Are declaración de obra nueva por antigüedad and 'prescription' the same thing?",
        a: "People often speak colloquially about a construction being 'prescribed', but it is technically more correct to speak of the declaración de obra nueva por antigüedad and of the possible time limitation of the public powers to restore planning legality. To use this route, the age and the planning status of the property must be studied, and it must be checked that it does not fall into any of the cases in which that time limitation legally does not operate.",
      },
      {
        q: "Does declaring a declaración de obra nueva por antigüedad mean legalising the building?",
        a: "No. They are different matters. Legalisation is a planning procedure aimed at accrediting or achieving that a development complies with the applicable planning regulations. The declaración de obra nueva por antigüedad is a procedure intended, when its requirements are met, to document and incorporate a building into the title and the Land Registry. Its registration does not automatically make the construction legal under planning law.",
      },
      {
        q: "When can a declaración de obra nueva por antigüedad be processed?",
        a: "A specific completion date of the construction must be provable, and it must be checked that the legally applicable period has elapsed without restoration measures that would prevent this route. In the Canary Islands there is generally a four-year period for certain developments, but there are exceptions in which the restoration power is not subject to a time limit. That is why we review each plot before confirming that the procedure is viable.",
      },
      {
        q: "How is the age proven?",
        a: "Age can be proven by the legally admitted means. Historical aerial photographs are an especially useful source because they allow an objective check of whether the construction already existed on a given date, although they are not the only possible means.",
      },
      {
        q: "What if the applicable period has not yet elapsed?",
        a: "If the period needed to use the obra nueva por antigüedad route has not yet elapsed, we will not be able to process it through this procedure at that time. We can study the property and check whether another planning or registry route is applicable.",
      },
    ],
  },
  {
    slug: "ampliacion-obra-nueva",
    shortName: "Extension of new construction",
    name: "Technical documentation for extension of new construction",
    seoTitle: "Extension of new construction in the Canary Islands | MideFinca",
    metaDescription:
      "Extended your home and it's not in the deed? Measurement, plans and technical documentation for the extension of new construction in the Canary Islands.",
    excerpt: "Your extension measured, drawn and documented so it can be added to the deed.",
    h1: "Documentation for extension of new construction in the Canary Islands",
    intro:
      "You enclosed a terrace, added a bedroom, built a second floor or an annex… and in the deed and Catastro the home still has its old square metres. The extension of new construction is the channel to document those metres, and it starts by measuring and drawing them correctly.",
    when: [
      "Your home has more built square metres than appear in the deed or Catastro.",
      "You are going to sell or mortgage and reality doesn't match the documentation.",
      "You want to update your property's declared built surface area.",
    ],
    solves:
      "While the extension is not recorded, the documented property and the real one are two different things, and that complicates sales, valuations and inheritances. We produce the measurement and the technical certificate the notary needs to process the extension; the deed and registration are up to the notary and the Land Registry.",
    includes: [
      "Technical report.",
      "Complete measurement of the home, including the extended part.",
      "Updated plans with the real built surface area.",
      "Descriptive technical certificate of the extension.",
      "Measurement certificate.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA) or equivalent graphic documentation, where applicable.",
    ],
    needs: [
      "The home's deed and cadastral reference.",
      "Access to all rooms, including the extended ones.",
      "Documentation from the time of the extension, if you kept it.",
    ],
    receive: [
      "Technical report.",
      "Measurement certificate.",
      "Certificate of age.",
      "Certificate or habitability documentation, where applicable.",
      "Plans.",
      "Georeferenced documentation.",
      "Positive Informe de Validación Gráfica Alternativa (IVGA), where applicable.",
    ],
    faq: [
      {
        q: "Can I declare an extension made many years ago?",
        a: "In many cases yes, and provable age is a key piece of the file. Viability and requirements depend on the case; the measurement and the certificate are the basis in any of them.",
      },
      {
        q: "What happens if the extension is in a property under horizontal division (a condominium or apartment building)?",
        a: "When the property forms part of a horizontal division (división horizontal), the constitutive title, the community statutes and which elements or surface areas are affected by the extension must be reviewed. If the modification affects common elements, participation quotas or the description contained in the constitutive title, the authorisation of the owners' community and the corresponding notarial formalisation may be required. We review the documentation beforehand to tell you what consent will be needed in your case.",
      },
      {
        q: "Does Catastro update itself afterwards?",
        a: "Not automatically. After the deed, the alteration is usually filed with Catastro with the corresponding documentation. We explain how each step fits together.",
      },
    ],
  },
  {
    slug: "certificado-antiguedad",
    shortName: "Certificate of age",
    name: "Certificate of age of constructions",
    seoTitle: "Certificate of age of constructions in the Canary Islands | MideFinca",
    metaDescription:
      "Technical certificate of age of buildings in the Canary Islands: measurement, historical photo-interpretation and documentary evidence to prove since when a construction has existed.",
    excerpt: "Technically prove since when a construction has existed, with measurement and historical evidence.",
    h1: "Certificate of age of constructions in the Canary Islands",
    intro:
      "Proving the age of a construction is decisive in many proceedings: declarations of new construction, regularisation files and sales. We produce technical certificates that study the historical evidence of the construction, such as aerial photography from different periods, old mapping and the documentation provided, to determine since when the building is recorded.",
    when: [
      "You need to demonstrate that a construction has existed for years.",
      "You are asked for it for a declaration or extension of new construction.",
      "An administrative file or a sale requires proving the construction date.",
      "Catastro or the deed don't record a building that has stood for decades.",
    ],
    solves:
      "Age is not declared: it is proven. Our certificate documents the measured current state and crosses it with historical orthophotos and other sources, so the conclusion about age rests on technical evidence, not on assertions.",
    includes: [
      "Study of historical aerial photography and mapping from different periods.",
      "Analysis of the documentation provided by the owner.",
      "Technical certificate with the evidence and the conclusion about the age.",
      "If the assignment also requires measuring the construction, the measurement is quoted as complementary work.",
    ],
    needs: [
      "Cadastral reference and deed of the property.",
      "Access to the construction.",
      "Any period evidence: photos, invoices, permits, old receipts.",
    ],
    receive: [
      "Technical certificate of age with the documented conclusion.",
      "Annexes with the historical evidence analysed.",
    ],
    faq: [
      {
        q: "Does the certificate guarantee the construction will be regularised?",
        a: "The certificate technically proves the age according to the available evidence. The legal or administrative effects of that age depend on the applicable regulations and on each file; other professionals and administrations are involved in those.",
      },
      {
        q: "What if the aerial photos don't go back as far as I need?",
        a: "We state it clearly in the certificate: the conclusion is limited to what the evidence allows us to affirm. A serious certificate says how far the proof reaches, not how far it would be convenient for it to reach.",
      },
    ],
  },
  {
    slug: "certificado-habitabilidad",
    officialLink: {
      label: "Official text of Decree 117/2006 of 1 August (Official Gazette of the Canary Islands, in Spanish):",
      href: "https://www.gobiernodecanarias.org/boc/2006/161/001.html",
    },
    shortName: "Certificate of habitability",
    name: "Certificate of habitability and dwelling measurement",
    seoTitle: "Certificate of habitability in the Canary Islands | MideFinca",
    metaDescription:
      "Dwelling measurement and technical certificate of habitability in the Canary Islands: usable and built surface areas, dwelling conditions and technical documentation for your procedures.",
    excerpt: "Real measurement of your home and technical certificate of its habitability conditions.",
    h1: "Certificate of habitability and dwelling measurement in the Canary Islands",
    intro:
      "In the Canary Islands, the habitability conditions of dwellings are regulated by Decree 117/2006 of 1 August. The rule establishes the minimum conditions dwellings must meet and distinguishes different cases according to the age of the property and the type of intervention: depending on the case, Annex I or Annex II of the Decree may apply. Our certificate technically describes the dwelling as it is, with its usable and built surface areas, the layout and the lighting and ventilation conditions of the rooms, in accordance with that rule.",
    when: [
      "You are asked to accredit the habitability conditions by the planning department (Urbanismo), the notary or another body within a procedure related to the dwelling.",
    ],
    solves:
      "This certificate provides the technical documentation needed for certain first-occupation procedures before the planning department, and the habitability documentation that may be required for contracting or regularising certain supplies, such as water or electricity, depending on the applicable procedure. It also replaces the surface figures that circulate without ever having been measured with an in-situ measurement of the dwelling, room by room, with a plan and a record of the observed conditions.",
    includes: [
      "Descriptive technical report.",
      "Complete in-situ measurement of the dwelling.",
      "Computation of usable and built surface area according to the applicable criteria.",
      "Plans of the dwelling with the measured layout.",
      "Descriptive certificate of the observed habitability conditions.",
    ],
    needs: [
      "Cadastral reference and deed or registry extract.",
      "Access to all rooms of the dwelling.",
      "Indication of the procedure it is needed for, to adjust the certificate's content.",
    ],
    receive: [
      "Technical report.",
      "Habitability certificate with the observed conditions.",
      "Plans.",
    ],
    faq: [
      {
        q: "Is this certificate the official 'cédula de habitabilidad'?",
        a: "No. The cédula de habitabilidad, where it exists, is an administrative document issued by the competent administration. Our certificate is the technical measurement and conditions document usually required as part of those and other files.",
      },
      {
        q: "What is the difference between Annex I and Annex II of Decree 117/2006?",
        a: "The Decree distinguishes cases according to the age of the property and the type of intervention, and each annex sets out different conditions. Which annex applies depends on the specific case: we check it during the review of your dwelling before issuing the documentation.",
      },
      {
        q: "When might this certificate be needed?",
        a: "Usually when the planning department, the notary or another body requests it within a procedure related to the dwelling, or when a procedure for contracting or regularising supplies requires habitability documentation.",
      },
      {
        q: "What is reviewed during the visit?",
        a: "The dwelling is measured room by room, the layout and the lighting and ventilation conditions are checked, and the observed state is documented. With that data we prepare the technical report and the certificate.",
      },
    ],
  },
  {
    slug: "certificado-superficies",
    shortName: "Certificate of surface areas",
    name: "Certificate of surface areas of properties",
    seoTitle: "Certificate of surface areas in the Canary Islands | MideFinca",
    metaDescription:
      "Technical certificate of usable and built surface area for homes, commercial premises and buildings in the Canary Islands. Real in-situ measurement for sales, valuations and discrepancies.",
    excerpt: "The real usable and built surface area of your property, measured on site and certified.",
    h1: "Certificate of surface areas of properties in the Canary Islands",
    intro:
      "The listing says one figure, Catastro another and the deed a third. The certificate of surface areas brings order: we measure the property on site, room by room, and certify its usable and built surface area with a plan backing every number.",
    when: [
      "You are going to sell and want to advertise the square metres the property really has.",
      "You are buying or have bought and the real surface area doesn't match what was agreed.",
      "A valuation or expert report needs a reliable measurement as a starting point.",
      "Catastro or the deed record a surface area that doesn't correspond to reality.",
      "You need the surface computation for a division, segregation or distribution.",
    ],
    solves:
      "Most surface figures in circulation have never been measured: they come from old plans, approximate declarations or data carried over between documents. The certificate of surface areas replaces them with a real measurement, with the computation criteria explained and the geometry drawn.",
    includes: [
      "Complete on-site measurement of the property, room by room.",
      "Computation of usable and built surface area according to the applicable criteria, including common elements where applicable.",
      "Plan of the measured layout with the surface area of each room.",
      "Technical certificate with the measurement result and the criteria used.",
      "Comparison with the Catastro and deed surface areas, if you provide the documentation.",
    ],
    needs: [
      "Access to all rooms of the property.",
      "Cadastral reference.",
      "Deed or registry extract, if you want the documentary comparison.",
      "Indication of the certificate's purpose, to adjust its content.",
    ],
    receive: [
      "Technical report.",
      "Certificate of surface areas with the room-by-room breakdown.",
      "Plan of the measured layout.",
      "Surface area comparison: measured, cadastral and registered, where applicable.",
    ],
    faq: [
      {
        q: "What's the difference between usable and built surface area?",
        a: "Usable area is what you can walk on and use; built area adds walls, installations and, depending on the case, the proportional share of common elements. The certificate states both and the criteria used to compute them.",
      },
      {
        q: "Does this certificate change the surface area in Catastro?",
        a: "Not automatically. It is the technical evidence of the real surface area; amending Catastro requires its own procedure, in which this document is usually the basis.",
      },
      {
        q: "Can I use it to claim square metres in a sale?",
        a: "It is the objective measurement of the property, which is the starting technical evidence. The claim itself and its viability are for your lawyer to assess.",
      },
    ],
  },
  {
    slug: "certificado-estructural",
    shortName: "Structural certificate",
    name: "Certificate of structural condition of buildings",
    seoTitle: "Structural condition certificate for buildings in the Canary Islands | MideFinca",
    metaDescription:
      "Technical report on the structural condition of homes and buildings in the Canary Islands: inspection, observed damage, assessment of its extent and recommendations before buying or renovating.",
    excerpt: "Technical assessment of the structure's condition: observed damage, extent and recommendations.",
    h1: "Certificate of structural condition in the Canary Islands",
    intro:
      "A crack can be a paint problem or the symptom of something serious. The certificate of structural condition documents how the property's structure is (columns, beams, slabs, walls and visible foundations), what damage it shows and how far it goes, so you can decide with technical information rather than hunches.",
    when: [
      "You are going to buy an old property and want to know the condition of its structure.",
      "Cracks, fissures, deformations or spalling have appeared and you don't know if they are serious.",
      "You are planning a renovation affecting structural elements and need to know the starting point.",
      "An administration, insurer or owners' association requires a report on the building's condition.",
      "After an incident, you need the structural damage technically documented.",
    ],
    solves:
      "Uncertainty about a structure paralyses decisions: purchases that don't close, renovations that don't dare to start, arguments about how serious a crack is. The certificate replaces it with a documented technical inspection that distinguishes the cosmetic from the structural and sets out the reasonable next steps.",
    includes: [
      "Visit and visual inspection of the accessible structural elements.",
      "Description of the property's structural system.",
      "Photographic record and location of the observed damage: fissures, cracks, deformations, damp or corrosion.",
      "Technical assessment of the extent of the damage and the overall situation.",
      "Recommendations: actions, monitoring or, where appropriate, testing or a detailed structural study.",
    ],
    needs: [
      "Access to all areas of the property, including roof, basement or common areas where applicable.",
      "Documentation of the building, if it exists: project, plans, previous reports.",
      "Indication of the reason for the assignment, to focus the inspection.",
    ],
    receive: [
      "Technical certificate with the observed structural condition and the documented damage.",
      "Assessment of the extent of the damage and recommendations for action.",
      "Photographic and graphic annex of the inspection.",
    ],
    faq: [
      {
        q: "Does this certificate guarantee the structure has no problems?",
        a: "It documents what was observed in a visual inspection: it certifies the apparent condition and the damage detected, with its assessment. If the evidence advises testing, trial pits or structural calculation, we say so expressly in the recommendations.",
      },
      {
        q: "Is it the same as the ITE (building inspection)?",
        a: "No. The Inspección Técnica de Edificios, where it is mandatory, is an administrative document with regulated content. This certificate is a technical report on the structural condition; if what you need is the ITE, tell us and we'll confirm the viability of the assignment.",
      },
      {
        q: "Can it be used for a claim against an insurer or a builder?",
        a: "It is an objective technical document describing the damage and its assessment, commonly used as starting evidence in claims. The legal strategy is up to your lawyer.",
      },
    ],
  },
  {
    slug: "certificado-eficiencia-energetica",
    shortName: "Energy performance certificate",
    name: "Energy performance certificate",
    seoTitle: "Energy performance certificate in the Canary Islands | MideFinca",
    metaDescription:
      "Energy performance certificate for homes and premises in the Canary Islands: energy rating, label and official registration to sell or rent your property.",
    excerpt: "Your property's official energy rating, with label and registration, to sell or rent.",
    h1: "Energy performance certificate in the Canary Islands",
    intro:
      "To sell or rent a home or commercial premises you need the energy performance certificate: the document rating the property from A to G according to its consumption and emissions. We visit the property, take the data of its envelope and installations, and prepare and register the certificate so you have the label on time.",
    when: [
      "You are going to sell a property and the notary will ask you for the energy label.",
      "You are going to rent and the listing must include the rating.",
      "Your previous certificate has expired or become outdated after a renovation.",
      "You want to know your home's real rating and which improvements would raise it.",
      "Application for certain subsidies or grants related to energy efficiency, when the governing rules require an energy performance certificate.",
    ],
    solves:
      "Without the certificate there is no correct listing and no signing: it is a mandatory document and its absence can delay or complicate the transaction. We prepare it with the visit and the property's real data, register it with the competent body and hand you the label ready for advertising and signing.",
    includes: [
      "Visit to the property and data capture: enclosures, openings, installations and orientation.",
      "Calculation of the energy rating with the official procedures.",
      "Energy performance certificate with rating, consumption and emissions.",
      "Proposal of improvement measures, in line with the regulated content.",
      "Registration of the certificate with the competent body of the Canary Islands and issue of the label.",
    ],
    needs: [
      "Access to the whole property, including the installations.",
      "Cadastral reference and basic registry data.",
      "Documentation of recent renovations or installations, if available.",
    ],
    receive: [
      "Registered energy performance certificate.",
      "Energy label for the listing and the signing.",
      "List of assessed improvement measures for the property.",
    ],
    faq: [
      {
        q: "Is it mandatory to sell or rent?",
        a: "Yes, as a general rule the regulations in force require it for the sale and rental of properties, subject to specific exceptions. If you think your case may be exempt, ask us before ruling it out.",
      },
      {
        q: "How long is it valid?",
        a: "The certificate is valid for a maximum of ten years, and it is worth updating it if renovations have changed the rating. We'll tell you whether yours is still valid.",
      },
      {
        q: "Who can sign it?",
        a: "Technicians with the qualifying degree the regulations require; in our case, the signature belongs to the chartered technical direction identified in this website's legal notice.",
      },
    ],
  },
  {
    slug: "primera-ocupacion-fuera-ordenacion",
    shortName: "First occupation (non-conforming buildings)",
    name: "First occupation of legally non-conforming buildings",
    seoTitle: "First occupation of non-conforming buildings in the Canary Islands | MideFinca",
    metaDescription:
      "Technical documentation to process the first occupation of buildings in fuera de ordenación status (legally non-conforming planning status) in the Canary Islands, when the planning regime of the property allows it.",
    excerpt: "Technical documentation for the first occupation of buildings in fuera de ordenación status, when the planning regime allows it.",
    h1: "First occupation of buildings in fuera de ordenación status",
    intro:
      "We prepare the technical documentation needed to process the first occupation or use of buildings that are in fuera de ordenación status (a legally non-conforming planning status), when the planning regime of the property allows the procedure. We study the available background, the current state of the building and the required technical conditions before preparing the documentation.",
    when: [
      "You are asked for first occupation or use documentation for a procedure involving the property.",
      "You need to accredit the building's conditions to contract or regularise utilities.",
      "You are going to sell and are asked to accredit the situation and conditions of the building.",
      "Your building is in fuera de ordenación status and you need to document its current state.",
    ],
    solves:
      "When a building is fuera de ordenación, any procedure requires documenting precisely what exists, since when and in what conditions. The prior technical study determines whether the property's planning regime allows the first occupation procedure and what documentation may be required in your case.",
    includes: [
      "Study of the background and available documentation of the property.",
      "Visit and check of the current state of the building.",
      "Measurement of the building, where applicable.",
      "Descriptive technical report.",
      "Habitability documentation, for dwellings and where applicable.",
      "Proof of age, where applicable.",
    ],
    needs: [
      "The property's title deed and cadastral reference.",
      "The existing documentation: old licences, files, plans.",
      "Full access to the building.",
    ],
    receive: [
      "Technical report on the current state and the background of the property.",
      "The certificates applicable to the case: measurement, age or habitability.",
      "Plans of the building.",
    ],
    faq: [
      {
        q: "Is the granting of first occupation guaranteed?",
        a: "No. The decision belongs to the competent town hall, which assesses the file according to the applicable planning regulations. Our work is to prepare the technical documentation the procedure requires, after checking that the property's planning regime allows attempting it.",
      },
      {
        q: "What does it mean that a building is 'fuera de ordenación'?",
        a: "That it does not conform to the current planning regulations, usually because it was built under earlier rules or without them. That status conditions which procedures and which interventions are possible on the property, and it must be studied individually.",
      },
      {
        q: "What documentation may the town hall ask for?",
        a: "It depends on the municipality and the case: it usually includes a technical report, plans of the current state, a certificate of age and, for dwellings, habitability documentation. We check this before preparing the file.",
      },
    ],
  },
  {
    slug: "informe-uso-consolidado",
    shortName: "Consolidated use report",
    name: "Report for declaration of consolidated use",
    seoTitle: "Report for declaration of consolidated use in the Canary Islands | MideFinca",
    metaDescription:
      "Technical documentation to accredit the characteristics, age and conditions of the existing use of a building in the Canary Islands, when a consolidated use must be processed or accredited.",
    excerpt: "Technical documentation of the characteristics, age and conditions of your building's existing use.",
    h1: "Report for declaration of consolidated use",
    intro:
      "We produce the technical documentation needed to accredit the characteristics, age and conditions of the existing use of a building when it is necessary to process or accredit a uso consolidado (a consolidated use) in accordance with the applicable planning regulations.",
    when: [
      "An administration or a procedure asks you to accredit the consolidated use of the property.",
      "You need to demonstrate that the building has been used in a specific way for years.",
      "You are going to sell or regularise and the current use must be technically documented.",
      "The real use of the property does not match the one in the documentation.",
    ],
    solves:
      "Accrediting a consolidated use requires demonstrating with evidence what use the building has, since when and in what conditions. The technical report documents that reality objectively, so that the administrative recognition, when necessary, has a solid technical basis.",
    includes: [
      "Study of the property's documentation and background.",
      "Visit and check of the building's current use.",
      "Measurement and plans of the current state, where applicable.",
      "Analysis of historical evidence of the use: aerial photography and period documentation.",
      "Technical report with the characteristics, age and conditions of the existing use.",
    ],
    needs: [
      "The property's title deed and cadastral reference.",
      "Documentation accrediting the use over time, if you have it: contracts, receipts, old licences.",
      "Full access to the building.",
    ],
    receive: [
      "Technical report on the existing use with the evidence analysed.",
      "Plans of the current state, where applicable.",
      "Documentary and photographic annexes.",
    ],
    faq: [
      {
        q: "What is a consolidated use (uso consolidado)?",
        a: "In general terms, a use that has existed continuously since a previous planning situation and that the regulations may come to recognise. Its existence must be studied individually and its recognition, when necessary, belongs to the Administration.",
      },
      {
        q: "Does the report guarantee recognition of the use?",
        a: "No. The report technically documents the reality of the use with evidence. The administrative recognition of the consolidated use, where applicable, belongs to the competent Administration in accordance with the applicable planning regulations.",
      },
    ],
  },
  {
    slug: "cedula-urbanistica",
    shortName: "Cédula urbanística",
    name: "Cédula urbanística (official planning information)",
    seoTitle: "Cédula urbanística application in the Canary Islands | MideFinca",
    metaDescription:
      "We prepare and process the cédula urbanística application (official planning information) in the Canary Islands: land classification, parameters, uses and building conditions of a parcel or property.",
    excerpt: "We prepare and process the application for official planning information about your parcel or property.",
    h1: "Cédula urbanística in the Canary Islands",
    intro:
      "We prepare and manage the documentation needed to request official planning information about a parcel or property: land classification and categorisation, planning parameters, permitted uses, building conditions and any other applicable provisions.",
    when: [
      "You want to know what you can build or what uses a parcel allows before buying it or designing.",
      "A notary, a bank or an administration asks you for official planning information about the property.",
      "You need to know the land classification and the parameters applicable to your parcel.",
      "You are starting a project and want to confirm the planning conditions from the outset.",
    ],
    solves:
      "Before buying, designing or building it is advisable to know what the planning rules say about the parcel: what can be built, with what parameters and which uses are allowed. The cédula urbanística or planning information is the official document accrediting this, and its application requires correctly identifying the property and its documentation.",
    includes: [
      "Identification and documentation of the parcel: cadastral reference, graphic representation and delimitation.",
      "Preparation and submission of the cédula urbanística or planning information application to the town hall.",
      "Review of the municipal reply and explanation of its content in clear terms.",
      "Check of the consistency between the planning information and the reality of the parcel.",
    ],
    needs: [
      "The parcel's cadastral reference.",
      "Title deed or registry extract, if you have one.",
      "The purpose of the enquiry, to focus the application.",
    ],
    receive: [
      "The submitted application with the corresponding technical documentation.",
      "The planning information issued by the town hall, once resolved.",
      "A clear explanation of what that information means for your parcel.",
    ],
    faq: [
      {
        q: "Does MideFinca issue the cédula urbanística?",
        a: "No. The cédula urbanística or planning information is issued by the competent town hall. We prepare and process the application, provide the technical documentation of the parcel and explain the content of the municipal reply.",
      },
      {
        q: "How long does the town hall take to reply?",
        a: "It depends on the municipality and its workload. We cannot guarantee administrative deadlines; we do ensure the application is correctly prepared from the start.",
      },
      {
        q: "Does it tell me whether I can build?",
        a: "It is the official document informing about the land classification, permitted uses and building parameters. It is the starting reference, although the specific viability of a project is confirmed with its technical project and its licence.",
      },
    ],
  },
  {
    slug: "alineaciones-rasantes",
    shortName: "Alineaciones y rasantes",
    name: "Alineaciones y rasantes (building lines and official levels)",
    seoTitle: "Alineaciones y rasantes in the Canary Islands | MideFinca",
    metaDescription:
      "Technical and topographic documentation to define or officially request the alineación (building line) of a parcel and the rasantes (official levels) conditioning the future building in the Canary Islands.",
    excerpt: "Technical and topographic documentation to define or request your parcel's building line and official levels.",
    h1: "Alineaciones y rasantes in the Canary Islands",
    intro:
      "We prepare the technical and topographic documentation needed to define or officially request the alineación of a parcel (its building line in relation to the street) and the cotas or rasantes (the official levels) that condition the future building.",
    when: [
      "You are going to build and the project or the town hall requires knowing the official building line.",
      "You need to know at what level the future building's reference plane must sit.",
      "You are asked for the alineaciones y rasantes within a licence procedure.",
      "The parcel borders the public road and its position relative to it must be defined.",
    ],
    solves:
      "The alineación defines how far the building may reach in relation to the street, and the rasante sets the reference level on which it is built. Knowing them precisely and with official backing is essential to design and to prevent the licence from being delayed by position or level errors.",
    includes: [
      "Topographic survey of the parcel and its immediate surroundings in relation to the street.",
      "Technical and topographic documentation for the definition or application of alineaciones and rasantes.",
      "Preparation and submission of the application to the town hall, where applicable.",
      "Plans with the resulting building line and levels.",
    ],
    needs: [
      "Cadastral reference and delimitation of the parcel.",
      "Access for the topographic survey.",
      "The file or project it is intended for, if it exists.",
    ],
    receive: [
      "Topographic documentation of the parcel in relation to the street.",
      "Plans with the building line and levels.",
      "The municipal resolution, when the procedure requires it and the town hall issues it.",
    ],
    faq: [
      {
        q: "Who issues the official alineaciones y rasantes?",
        a: "The competent town hall, when the procedure requires it. Our work is to prepare the technical and topographic documentation, submit the application and provide the precise measurement of the parcel and the street.",
      },
      {
        q: "Is the alineación the same as my plot's boundary?",
        a: "No. The alineación is the line marking the building's position relative to the public road according to the planning rules; the boundary is the limit of your property. They may coincide or not, and it is advisable to know both before designing.",
      },
    ],
  },
  {
    slug: "comunicacion-previa-obra",
    shortName: "Prior notification of works",
    name: "Prior notification of works (comunicación previa)",
    seoTitle: "Prior notification of works in the Canary Islands | MideFinca",
    metaDescription:
      "Technical documentation for works that may be carried out through comunicación previa (prior notification) in the Canary Islands, checking first which procedure applies according to the works and the municipal rules.",
    excerpt: "Technical documentation for works that may be processed by prior notification, checking first which procedure applies.",
    h1: "Prior notification of works in the Canary Islands",
    intro:
      "We prepare the technical documentation needed to process works that may be carried out through comunicación previa (prior notification), checking beforehand which procedure applies according to the type of work and the applicable municipal regulations.",
    when: [
      "You want to renovate and don't know whether you need a licence or a prior notification is enough.",
      "The town hall has told you your works can be processed by prior notification.",
      "You are going to carry out minor works and want to submit the documentation correctly from the start.",
    ],
    solves:
      "Not every small work qualifies for prior notification and not every renovation requires a licence: the classification depends on the type of work and on each town hall's ordinance. Checking it beforehand avoids filing the wrong procedure, with its delays and possible consequences.",
    includes: [
      "Check of the procedure applicable to the works according to the municipal regulations.",
      "Technical documentation of the works: report, plans and measurement, as the case requires.",
      "Preparation and submission of the prior notification.",
      "Works budget when the procedure requires it.",
    ],
    needs: [
      "A description of the works you want to carry out.",
      "Cadastral reference and property details.",
      "Existing plans or documentation, if any.",
    ],
    receive: [
      "The technical documentation of the works.",
      "The prior notification submitted to the town hall.",
      "Confirmation of the procedure applicable to your case.",
    ],
    faq: [
      {
        q: "Are 'minor works' and prior notification the same thing?",
        a: "Not always. The classification of a work as minor or its admission through prior notification depends on each municipality's ordinance and on the type of work. That is why we check which procedure applies before filing anything.",
      },
      {
        q: "Can I start the works when I file the notification?",
        a: "It depends on the applicable municipal regulations and on the content of the notification. We will tell you what applies in your case according to the ordinance and the municipal reply, without assuming a specific timeframe.",
      },
      {
        q: "What if my works need a licence?",
        a: "We tell you before starting and then prepare the documentation for the applicable procedure, including the technical project when the works require it.",
      },
    ],
  },
  {
    slug: "licencia-obra-mayor",
    shortName: "Major works licence",
    name: "Major works licence (licencia de obra mayor)",
    seoTitle: "Major works licence in the Canary Islands | MideFinca",
    metaDescription:
      "Technical project and documentation to apply for a major works licence in the Canary Islands: new construction, extensions, renovations, refurbishments and structural interventions, as the case may be.",
    excerpt: "Technical project and documentation to apply for the licence when your works require it.",
    h1: "Major works licence in the Canary Islands",
    intro:
      "We draw up the technical project and documentation needed to apply for a planning works licence when the works require a technical project and prior municipal authorisation.",
    when: [
      "You are going to build a new building.",
      "You want to extend an existing construction.",
      "You are planning a renovation affecting the structure or the configuration of the property.",
      "You are going to refurbish a building or carry out structural interventions.",
      "The town hall has told you your works need a major works licence.",
    ],
    solves:
      "When the works require a licence, the file needs a complete and correctly prepared technical project: measurements, plans, report and compliance with the applicable regulations. A file well prepared from the start avoids correction requests and delays during processing.",
    includes: [
      "Preliminary study of the parcel and the applicable planning regulations.",
      "Survey and measurement of the current state, where applicable.",
      "Drafting of the technical project: report, plans and required documentation.",
      "Submission of the licence application to the town hall.",
      "Attention to the technical requirements of the file, within the contracted scope.",
    ],
    needs: [
      "The idea or scope of the works you want to carry out.",
      "Cadastral reference, title deed and property details.",
      "Existing prior documentation: plans, old licences, projects.",
    ],
    receive: [
      "The complete technical project for the application.",
      "The licence application submitted with its documentation.",
      "The file documentation, including the corrections attended where applicable.",
    ],
    faq: [
      {
        q: "Is the granting of the licence guaranteed?",
        a: "No. The licence is granted or denied by the town hall according to the planning regulations. Our work is to prepare a correct and complete technical project, which is the condition for the file to be processed favourably.",
      },
      {
        q: "Which works need a major works licence?",
        a: "Depending on the case and the municipal ordinance: new construction, extensions, certain renovations, refurbishments, structural interventions and other works subject to licence. We check this for your specific works before preparing anything.",
      },
      {
        q: "How long does the licence take?",
        a: "Timeframes depend on the town hall and the file. What we do control is that the technical documentation goes in complete and well prepared, so as not to add delays on our side.",
      },
    ],
  },
  {
    slug: "division-horizontal-canarias",
    shortName: "Horizontal division",
    name: "Horizontal division of buildings (división horizontal)",
    seoTitle: "Horizontal division (división horizontal) in the Canary Islands | MideFinca",
    metaDescription:
      "Measurement and technical documentation to establish or modify the horizontal division of a building in the Canary Islands: description of each unit, floor areas, common elements and participation quotas.",
    excerpt: "The technical basis for dividing a building into independent units: measurement, description, floor areas and quotas.",
    h1: "Horizontal division of buildings in the Canary Islands",
    intro:
      "A building can be legally divided into independent units (flats, commercial premises, storage rooms or parking spaces) through the Spanish figure known as división horizontal, the horizontal division. MideFinca prepares the technical side: we measure the building and each unit, describe their floor areas and common elements and calculate the participation quotas, so that the notary and the Land Registry work with measured, coherent data.",
    when: [
      "You are going to divide an existing building into several independent units to sell or share them out.",
      "You are establishing the horizontal property regime for a new or existing building.",
      "An existing horizontal division must be modified: new units, changes of floor area or of quotas.",
      "The built reality does not match what the constitutive title describes.",
      "An inheritance, sale or company contribution requires each unit to be precisely delimited.",
    ],
    solves:
      "A horizontal division requires each unit to be described precisely: floor area, layout, common elements and participation quota. If those details are drafted without real measurement, discrepancies appear that later block sales, mortgages or distributions. Our job is to make the technical description match the measured reality.",
    includes: [
      "Full on-site measurement of the building and of each of its units.",
      "Calculation of usable and built floor area for each unit, including the share of common elements where applicable.",
      "Technical description of each unit and of the building's common elements.",
      "Calculation of participation quotas according to the criterion applicable to the case.",
      "Floor plans of each storey showing the delimitation of the units.",
    ],
    needs: [
      "Access to all units and common areas of the building.",
      "Deed, constitutive title or Land Registry extract (nota simple), if available.",
      "Previous plans or permits, if any.",
      "The purpose of the work: establish, modify or verify the division.",
    ],
    receive: [
      "Technical report.",
      "Storey plans with the units delimited and their floor areas.",
      "Statement of floor areas and participation quotas per unit.",
      "Georeferenced graphic documentation, where applicable.",
    ],
    faq: [
      {
        q: "Who formalises the horizontal division?",
        a: "The constitution or modification is formalised in a public deed before a notary and registered at the Land Registry (Registro de la Propiedad). MideFinca prepares the technical documentation (measurement, description of units, floor areas and quotas) on which that title is drafted.",
      },
      {
        q: "Can participation quotas be chosen freely?",
        a: "The quota is normally set in relation to the floor area or value of each unit relative to the whole, unless the title establishes a different criterion. We calculate the quotas from the real measurement and explain the criterion applied; the final legal drafting corresponds to the notary.",
      },
      {
        q: "Does it work for an old building that was never divided?",
        a: "Yes: we measure the building as it stands and document each existing unit. If there are also discrepancies with Catastro or the Land Registry, we indicate which additional procedures might be needed in your case.",
      },
    ],
    relatedServices: ["certificado-superficies", "declaracion-obra-nueva", "ampliacion-obra-nueva"],
    relatedProblems: ["dividir-edificio-en-unidades"],
    relatedGuides: ["que-es-division-horizontal"],
  },
  {
    slug: "cambio-de-uso-local-a-vivienda",
    shortName: "Change of use",
    name: "Change of use from commercial premises to dwelling",
    seoTitle: "Change of use from premises to dwelling in the Canary Islands | MideFinca",
    metaDescription:
      "Technical feasibility study and documentation to apply for a change of use from commercial premises to a dwelling in the Canary Islands: measurement, openings, ventilation and conditions under the municipal ordinance.",
    excerpt: "We check whether your premises meet the technical conditions to apply for a change of use to a dwelling.",
    h1: "Change of use from commercial premises to dwelling in the Canary Islands",
    intro:
      "Turning commercial premises into a dwelling does not start with the works: it starts with knowing whether the space and the planning rules allow it. We measure the premises, check their conditions (openings, natural light and ventilation, ceiling heights, layout and access) and prepare the technical documentation to apply for the change of use where the municipal procedure provides for it.",
    when: [
      "You own or plan to buy commercial premises and want to know whether they could become a dwelling.",
      "You need the technical feasibility report before deciding on the investment.",
      "The municipal procedure requires a project or technical documentation to apply for the change of use.",
      "The owners' community or its statutes may be affected by the new use.",
    ],
    solves:
      "Many operations fail because people buy or build before checking. The preliminary study answers the key question with data: whether the premises meet the technical conditions required of a dwelling and whether the planning rules and the ordinance of your municipality allow the change of use.",
    includes: [
      "Visit and full measurement of the premises.",
      "Checking of openings, natural light and ventilation, clear heights and layout.",
      "Review of the planning rules and the municipal ordinance applicable to the change of use.",
      "Technical feasibility report with the conditions detected and the constraints.",
      "Technical documentation for the application of the procedure that corresponds, according to the municipality.",
    ],
    needs: [
      "Access to the premises.",
      "Cadastral reference and, if you have it, the deed or a Land Registry extract.",
      "Statutes of the owners' community, if the premises are in a building under the horizontal property regime.",
    ],
    receive: [
      "Technical report.",
      "Plans of the measured premises and, where applicable, of the proposed layout.",
      "List of the technical and planning constraints detected.",
    ],
    faq: [
      {
        q: "Can any commercial premises be converted into a dwelling?",
        a: "No. It depends on the municipal planning rules, on the corresponding ordinance, on the physical conditions of the premises (openings, ventilation, ceiling heights) and, where applicable, on the owners' community. That is why the first step is the feasibility study, not the works.",
      },
      {
        q: "Does the feasibility report guarantee the change of use will be granted?",
        a: "No. The report documents the technical conditions and constraints of the case; the resolution of the procedure corresponds to the town hall. What it avoids is investing blindly.",
      },
      {
        q: "Which procedure is required?",
        a: "It depends on the municipality and the case: it may range from a responsible declaration or prior notification to a planning permit with a technical project. After the visit and the regulatory review we will tell you which procedure applies to your premises.",
      },
    ],
    relatedServices: ["certificado-habitabilidad", "licencia-obra-mayor", "certificado-superficies"],
    relatedProblems: ["convertir-local-en-vivienda"],
    relatedGuides: ["cambio-de-uso-local-a-vivienda-canarias"],
  },
];
